/* eslint-disable @typescript-eslint/naming-convention */
import React, { useCallback, useContext, useMemo } from "react";
import {
    SWIGGY_ONE_COMPONENT,
    SWIGGY_ONE_STATE,
    getErrorBucket,
    recordSwiggyOneNREvent,
} from "@swiggy-private/super-ui";
import { CartContext } from "@cart-client/contexts/CartContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

import { SwiggyPlanBullsEyeTGHalfCard } from "@swiggy-private/cart-ui";
import { ErrorBoundary } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

import { TGWrapperProps } from "./types";
import { SWIGGY_ONE_CONFETTI } from "@cart-client/constants/Coupon";
import {
    clickBullsEyeTgAddCta,
    clickBullsEyeTgSkipCta,
    impressionBullsEyeTgAddCta,
    impressionBullsEyeTgHalfcardMount,
    impressionBullsEyeTgSkipCta,
} from "@cart-client/analytics/events";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { isSwiggyOneLitePlan } from "@cart-client/helpers/SwiggyPlanBullsEyeTg";
import { getStrickedText } from "@swiggy-private/super-ui";
import M2CHalfcardContainer from "./M2CHalfcardContainer";

const SwiggyPlanBullsEyeTGWrapper: React.FC<TGWrapperProps> = (props) => {
    const {
        billDetailsCardResponse,
        showBillDetailsOneCardComms,
        setShowBillDetailsOneCardComms,
        planDetails,
        showConfetti,
        planAddedRef,
        showDelightPopup,
    } = props;

    const { doUpdateCart } = useContext(UpdateCartContext);
    const { cartState } = useContext(CartContext);
    const { clickEvent } = useContext(AnalyticsContext);

    const bottomSheetData = useMemo(() => {
        if (showBillDetailsOneCardComms) {
            return billDetailsCardResponse;
        }
    }, [showBillDetailsOneCardComms, billDetailsCardResponse]);

    /** Modifing the title by appending the `plan_info_text` and creating the context for analytics */
    const context = useMemo(() => {
        const { attribute_values, title = "", subtitles, note = "" } = bottomSheetData || {};
        const planInfoText = attribute_values?.plan_info_text || "";
        const formattedSubtitles = subtitles?.map((subtitle) => getStrickedText(subtitle, attribute_values)) || [];

        return JSON.stringify({
            plan_id: planDetails?.plan_id,
            title: `${title.split("{{")?.[0]}${planInfoText}}`,
            tag: note,
            subtitle: formattedSubtitles.join(" "),
        });
    }, [bottomSheetData, planDetails?.plan_id]);

    const closeBottomSheet = useCallback(
        (ctaText = "") => {
            setShowBillDetailsOneCardComms(false);
            clickBullsEyeTgSkipCta(clickEvent, context, ctaText);
        },
        [clickEvent, context, setShowBillDetailsOneCardComms],
    );

    const focusChangeSuperButton = useCallback(() => {
        if (planAddedRef && planAddedRef.current) {
            planAddedRef.current.autofocus = true;
        }
    }, [planAddedRef]);

    const addSuperFromTGBottomsheet = useCallback(
        async (ctaText = "") => {
            clickBullsEyeTgAddCta(clickEvent, context, ctaText);
            await doUpdateCart({
                ...cartState?.localCart,
                subscriptionItems: [
                    {
                        plan_id: planDetails?.plan_id?.toString() ?? "",
                        quantity: 1,
                    },
                ],
            });

            closeBottomSheet();
            showConfetti && showConfetti(SWIGGY_ONE_CONFETTI);
            focusChangeSuperButton();
        },
        [
            cartState?.localCart,
            clickEvent,
            closeBottomSheet,
            context,
            doUpdateCart,
            focusChangeSuperButton,
            planDetails?.plan_id,
            showConfetti,
        ],
    );

    const analytics = {
        impressionBullsEyeTgHalfcardMount,
        impressionBullsEyeTgAddCta,
        impressionBullsEyeTgSkipCta,
    };

    const isOneLite = useMemo(() => isSwiggyOneLitePlan(planDetails?.type || ""), [planDetails]);

    const onBullsEyeTGError = useCallback((error?: Error) => {
        recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.BULLS_EYE_TG, SWIGGY_ONE_STATE.ERROR_BOUNDARY, {
            errorName: error?.name || "",
            errorBucket: getErrorBucket(error),
        });
    }, []);

    // We have kept the existing component for BillsDetails MAKE IT FREE CTA, m2c card comms is moved to new component inside super-ui
    if (showBillDetailsOneCardComms && !isEmpty(bottomSheetData)) {
        recordSwiggyOneNREvent(SWIGGY_ONE_COMPONENT.BULLS_EYE_TG, SWIGGY_ONE_STATE.MOUNTED);
        return (
            <ErrorBoundary errorContent={null} errorFallback={onBullsEyeTGError}>
                <SwiggyPlanBullsEyeTGHalfCard
                    analytics={analytics}
                    bottomSheetData={bottomSheetData}
                    addSuperFromTGBottomsheet={addSuperFromTGBottomsheet}
                    closeBottomSheet={closeBottomSheet}
                    isOneLite={isOneLite}
                    context={context}
                />
            </ErrorBoundary>
        );
    }

    return <M2CHalfcardContainer showDelightPopup={showDelightPopup} />;
};

export default React.memo(SwiggyPlanBullsEyeTGWrapper);
